import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { problemMessage } from '../../api/baseApi'
import { useGetBillingOverviewQuery, useLazyGetLedgerQuery } from '../../api/operator'
import type { LedgerLine } from '../../api/operatorApi'
import { formatDate, formatMoney, formatMonth, todayISO } from '../../model/money'
import { operator } from '../../model/routes'
import { Loading, QueryError } from '../../ui/QueryState'
import { OperatorHeader } from './common'
import { shown } from '../../api/useIntent'

/**
 * Reconciliation across every account: who owes what, what falls due
 * soon, and what arrived — the three questions the bank statement is
 * held up against.
 */
export function OperatorBilling() {
  const overview = useGetBillingOverviewQuery()
  if (overview.isLoading) return <Loading label="Reading every account…" />
  if (overview.error || !overview.data) return <QueryError error={overview.error} title="Could not load billing" />
  const data = overview.data
  const overdueTotal = data.overdue.reduce((sum, account) => sum + account.overdue, 0)

  return (
    <Stack spacing={3}>
      <OperatorHeader title="Billing" subtitle={`Every account as of ${formatDate(data.asOf)}.`} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Figure label="Overdue" value={overdueTotal > 0 ? formatMoney(overdueTotal) : '—'} caption={data.overdue.length === 1 ? '1 account' : `${data.overdue.length} accounts`} alert={overdueTotal > 0} />
        <Figure label="Recurring, per month" value={formatMoney(data.monthlyRecurring)} caption="annual agreements divided by twelve" />
        <Figure label="Paying" value={`${data.monthlyCount} monthly · ${data.annualCount} annually`} />
      </Stack>

      <Section title="Overdue" empty="Nothing is overdue.">
        {data.overdue.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Overdue since</TableCell>
                <TableCell align="right">Overdue</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.overdue.map((account) => (
                <TableRow key={account.tenant} hover>
                  <TableCell>
                    <TenantLink tenant={account.tenant} name={account.name} />
                  </TableCell>
                  <TableCell>{formatDate(account.oldestOverdue)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>
                    {formatMoney(account.overdue)}
                  </TableCell>
                  <TableCell align="right">{formatMoney(account.balance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section title="Charging in the next 30 days" empty="Nothing charges in the next 30 days.">
        {data.upcoming.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.upcoming.map((upcoming) => (
                <TableRow key={`${upcoming.tenant}-${upcoming.charge.periodStart}`} hover>
                  <TableCell>
                    <TenantLink tenant={upcoming.tenant} name={upcoming.name} />
                  </TableCell>
                  <TableCell>
                    {formatDate(upcoming.charge.periodStart)} – {formatDate(upcoming.charge.periodEnd)}
                  </TableCell>
                  <TableCell>{formatDate(upcoming.charge.due)}</TableCell>
                  <TableCell align="right">{formatMoney(upcoming.charge.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section title="Received" empty="No payments in the last twelve months.">
        {data.received.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align="right">Payments</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.received.map((month) => (
                <TableRow key={month.month}>
                  <TableCell>{formatMonth(month.month)}</TableCell>
                  <TableCell align="right">{month.payments}</TableCell>
                  <TableCell align="right">{formatMoney(month.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Export />
    </Stack>
  )
}

function Figure({ label, value, caption, alert }: { label: string; value: string; caption?: string; alert?: boolean }) {
  return (
    <Paper sx={{ p: 2, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      <Typography variant="h2" sx={{ color: alert ? 'error.main' : 'text.primary' }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Paper>
  )
}

function Section({ title, empty, children }: { title: string; empty: string; children: React.ReactNode }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children || (
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      )}
    </Paper>
  )
}

function TenantLink({ tenant, name }: { tenant: string; name: string }) {
  return (
    <Link component={RouterLink} to={operator.tenantBilling(tenant)} underline="hover">
      {name}
    </Link>
  )
}

/**
 * Every statement line across tenants between two dates, as CSV — the
 * form a spreadsheet or the accounting system takes. Amounts are signed
 * the way a ledger reads them: owed positive, paid negative.
 */
function Export() {
  const today = todayISO()
  const [from, setFrom] = useState(`${today.slice(0, 7)}-01`)
  const [to, setTo] = useState(today)
  const [fetchLedger, ledger] = useLazyGetLedgerQuery()

  const download = async () => {
    const result = await fetchLedger({ from, to }).unwrap()
    const rows = [
      ['tenant', 'name', 'date', 'kind', 'description', 'amount', 'status', 'due', 'invoice', 'reference', 'void'],
      ...result.lines.map((entry: LedgerLine) => [
        entry.tenant,
        entry.name,
        entry.line.date,
        entry.line.kind,
        entry.line.kind === 'charge' ? `${entry.line.planName ?? ''} to ${entry.line.periodEnd ?? ''}` : (entry.line.reason ?? entry.line.method ?? ''),
        ((entry.line.debit ? entry.line.amount : -entry.line.amount) / 100).toFixed(2),
        entry.line.status ?? '',
        entry.line.due ?? '',
        entry.line.invoiceRef ?? '',
        entry.line.reference ?? '',
        entry.line.void ? entry.line.void.reason : '',
      ]),
    ]
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `ledger-${from}-to-${to}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h3">Export</Typography>
        {Boolean(ledger.error) && <Alert severity="error">{problemMessage(ledger.error, 'The ledger could not be read.')}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField label="From" type="date" size="small" value={from} onChange={(event) => setFrom(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="To" type="date" size="small" value={to} onChange={(event) => setTo(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <Button variant="outlined" disabled={!from || !to || ledger.isFetching} onClick={() => download().catch(shown)}>
            Download CSV
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
